import { useState, useEffect } from 'react';
import { useExperimentStore } from '../store/experimentStore';
import { useExperimentConfig } from '../store/experimentConfigStore';
import { useUiStore } from '../store/uiStore';
import { RefreshCcw, Power, CheckCircle2, Plus, Minus, Lock, Loader2 } from 'lucide-react';
import { gradeExperiment } from '../lib/grading';
import { useExperiment as useConvexConfig, useSaveLabData, useSubmitDiscussionAnswers, useDiscussionQuestions } from '../../../../utils/convexHooks';
import { useCurrentUser } from '../../../../components/AuthComponents';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Id } from '../../../../convex/_generated/dataModel';
import { autoGradeDiscussionAnswer } from '../../../../utils/gemini';

interface NumericInputProps {
    readonly label: string;
    readonly value: number;
    readonly onChange: (val: number) => void;
    readonly step: number;
    readonly min: number;
    readonly max: number;
    readonly unit: string;
    readonly disabled?: boolean;
}

function NumericConfigInput({ label, value, onChange, step, min, max, unit, disabled }: NumericInputProps) {
    const [tempValue, setTempValue] = useState(value.toString());

    // Sync with external store changes (like Reset)
    useEffect(() => {
        setTempValue(value.toString());
    }, [value]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setTempValue(val);
        const parsed = Number.parseFloat(val);
        if (!Number.isNaN(parsed)) {
            onChange(parsed);
        }
    };

    const handleBlur = () => {
        let parsed = Number.parseFloat(tempValue);
        if (Number.isNaN(parsed)) parsed = min;
        const clamped = Math.max(min, Math.min(max, parsed));
        onChange(clamped);
        setTempValue(clamped.toString());
    };

    const adjust = (delta: number) => {
        const newValue = Math.max(min, Math.min(max, value + delta));
        // Use toFixed to eliminate floating point garbage while keeping necessary precision
        const fixedValue = Number.parseFloat(newValue.toFixed(8));
        onChange(fixedValue);
        setTempValue(fixedValue.toString());
    };

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex justify-between">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</label>
                {disabled && <Lock className="w-3 h-3 text-muted-foreground/50" />}
            </div>
            <div className="relative flex items-center gap-2">
                <button
                    disabled={disabled}
                    onClick={() => adjust(-step)}
                    className="interactable-btn p-2 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors border border-border disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Minus className="w-4 h-4" />
                </button>
                <div className="relative flex-1">
                    <input
                        type="text"
                        inputMode="decimal"
                        value={disabled ? value : tempValue}
                        onChange={handleTextChange}
                        onBlur={handleBlur}
                        disabled={disabled}
                        className="w-full bg-background border border-border rounded-lg px-3 pr-10 py-2 text-sm font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-shadow disabled:opacity-75 disabled:cursor-not-allowed disabled:bg-muted"
                    />
                    <span className="absolute right-3 top-2 text-sm text-muted-foreground font-medium pointer-events-none">{unit}</span>
                </div>
                <button
                    disabled={disabled}
                    onClick={() => adjust(step)}
                    className="interactable-btn p-2 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors border border-border disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

export default function BuretteControls() {
    const addVolume = useExperimentStore((state) => state.addVolume);
    const resetExperiment = useExperimentStore((state) => state.resetExperiment);
    const isRunning = useExperimentStore((state) => state.isRunning);
    const labStage = useExperimentStore((state) => state.labStage);
    const isStopcockOpen = useExperimentStore((state) => state.isStopcockOpen);
    const setStopcockOpen = useExperimentStore((state) => state.setStopcockOpen);
    const setLabStage = useExperimentStore((state) => state.setLabStage);
    const volumeAdded = useExperimentStore((state) => state.volumeAdded);
    
    // For calculation and saving
    const titrationData = useExperimentStore((state) => state.titrationData);
    const setScore = useExperimentStore((state) => state.setScore);
    const discussionAnswers = useExperimentStore((state) => state.discussionAnswers);

    const user = useCurrentUser()?.user;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const idStr = searchParams.get('id');
    const experimentId = idStr ? (idStr as Id<"experimentConfigs">) : undefined;
    const configData = useConvexConfig(experimentId);
    const saveLabData = useSaveLabData();
    const submitDiscussionMutation = useSubmitDiscussionAnswers();
    const discussionQuestions = useDiscussionQuestions(experimentId);
    
    // UI states
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dynamic config
    const hclConcentration = useExperimentStore((state) => state.hclConcentration);
    const naohConcentration = useExperimentStore((state) => state.naohConcentration);
    const flaskVolume = useExperimentStore((state) => state.flaskVolume);
    const buretteVolume = useExperimentStore((state) => state.buretteVolume);
    const setHclConcentration = useExperimentStore((state) => state.setHclConcentration);
    const setNaohConcentration = useExperimentStore((state) => state.setNaohConcentration);
    const setFlaskVolume = useExperimentStore((state) => state.setFlaskVolume);
    const setBuretteVolume = useExperimentStore((state) => state.setBuretteVolume);
    const restoreDefaults = useExperimentStore((state) => state.restoreDefaults);
    
    // Experiment Configuration
    const currentConfig = useExperimentConfig((state) => state.currentConfig);
    const isTeacherMode = useExperimentConfig((state) => state.isTeacherMode);
    
    // If allowStudentConfig is false, they are disabled (except for teacher mode)
    const disableConfig = !isTeacherMode && currentConfig !== null && currentConfig.allowStudentConfig === false;

    const canTitrate = labStage === 'titrate' && isRunning;
    const pulseValve = useExperimentStore((state) => state.pulseValve);

    const handleAddVolume = (amount: number) => {
        if (!canTitrate) return;
        addVolume(amount);
        pulseValve();
    };

    const toggleStopcock = () => {
        if (!canTitrate) return;
        setStopcockOpen(!isStopcockOpen);
    };

    const dropBtn = "interactable-btn relative overflow-hidden rounded-xl font-bold text-white transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md hover:shadow-blue-500/20";
    const fastBtn = "interactable-btn relative overflow-hidden rounded-xl font-bold text-white transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md hover:shadow-indigo-500/20";

    const aiLoaderOverlay = isSubmitting && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-auto">
            <div className="bg-card dark:bg-gray-800 p-8 rounded-3xl shadow-[0_0_50px_rgba(79,70,229,0.2)] flex flex-col items-center max-w-sm w-full mx-4 border border-indigo-500/20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/10 to-transparent"></div>
                
                <div className="relative flex items-center justify-center mb-6 h-28 w-28">
                    <div className="absolute w-28 h-28 rounded-full border-t-2 border-indigo-400 border-r-2 border-transparent animate-spin"></div>
                    <div className="absolute w-24 h-24 rounded-full border-b-2 border-pink-400 border-l-2 border-transparent animate-spin-slow" style={{ animationDirection: 'reverse' }}></div>
                    <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center shadow-[0_0_30px_rgba(99,102,241,0.5)]">
                        <Loader2 className="w-10 h-10 text-indigo-400 animate-spin relative z-10" />
                    </div>
                </div>

                <h2 className="text-xl font-bold text-foreground dark:text-white mb-2 text-center relative z-10">AI Evaluator Active</h2>
                <p className="text-sm text-muted-foreground dark:text-gray-400 text-center mb-4 relative z-10">
                    Analyzing titration curve, evaluating your discussion answers, and generating personalized feedback...
                </p>
                
                <div className="w-full bg-muted dark:bg-gray-700 h-1.5 rounded-full overflow-hidden relative z-10 mb-4">
                    <div className="bg-indigo-500 h-full rounded-full transition-all" style={{ width: '100%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                </div>

                <button 
                    onClick={() => {
                        setIsSubmitting(false);
                        navigate('/student/dashboard');
                    }}
                    className="relative z-10 text-xs font-medium text-gray-400 hover:text-red-400 transition-colors border border-transparent hover:border-red-500/20 px-3 py-1.5 rounded-full"
                >
                    Taking too long? Return to Dashboard
                </button>
            </div>
        </div>,
        document.body
    );

    if (labStage === 'setup') {
        return (
            <div className="flex flex-col gap-4 py-2">
                {aiLoaderOverlay}
                <div className="flex items-center justify-between border-b border-indigo-500/20 pb-2 mb-1">
                    <div className="text-sm font-bold text-indigo-400">Initial Configuration</div>
                    {disableConfig && <span className="text-[10px] text-muted-foreground uppercase bg-muted px-2 py-0.5 rounded-full font-bold">Locked</span>}
                </div>

                <NumericConfigInput
                    label="NaOH Concentration (Burette)"
                    value={naohConcentration}
                    onChange={setNaohConcentration}
                    step={0.01}
                    min={0.01}
                    max={2}
                    unit="M"
                    disabled={disableConfig}
                />

                <NumericConfigInput
                    label="NaOH Volume (Burette)"
                    value={buretteVolume}
                    onChange={setBuretteVolume}
                    step={5}
                    min={5}
                    max={50}
                    unit="mL"
                    disabled={disableConfig}
                />

                <NumericConfigInput
                    label="HCl Concentration (Flask)"
                    value={hclConcentration}
                    onChange={setHclConcentration}
                    step={0.01}
                    min={0.01}
                    max={2}
                    unit="M"
                    disabled={disableConfig}
                />

                <NumericConfigInput
                    label="HCl Volume (Flask)"
                    value={flaskVolume}
                    onChange={setFlaskVolume}
                    step={5}
                    min={5}
                    max={100}
                    unit="mL"
                    disabled={disableConfig}
                />

                <button
                    onClick={restoreDefaults}
                    disabled={disableConfig}
                    className="interactable-btn flex items-center justify-center gap-2 mt-2 text-indigo-400 hover:text-indigo-300 text-xs font-medium px-3 py-2 rounded-lg border border-indigo-500/20 hover:bg-indigo-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <RefreshCcw className="w-3.5 h-3.5" /> Restore Defaults
                </button>
            </div>
        );
    }

    if (labStage !== 'titrate') {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-4 text-gray-500">
                {aiLoaderOverlay}
                <Power className="w-5 h-5 opacity-40" />
                <p className="text-xs font-medium text-center max-w-[180px]">
                    Controls unlock after filling burette &amp; flask
                </p>
                <button
                    onClick={resetExperiment}
                    className="interactable-btn flex items-center gap-2 mt-2 text-red-400/70 hover:text-red-400 text-xs font-medium px-3 py-1.5 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-all"
                >
                    <RefreshCcw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
                {/* Dropwise Controls */}
                <button
                    onClick={() => handleAddVolume(0.01)}
                    disabled={!canTitrate}
                    className={`${dropBtn} py-3 text-sm`}
                >
                    <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors" />
                    <span className="relative z-10 drop-shadow-md">+ 0.01 mL</span>
                </button>
                <button
                    onClick={() => handleAddVolume(0.1)}
                    disabled={!canTitrate}
                    className={`${dropBtn} py-3 text-base`}
                >
                    <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors" />
                    <span className="relative z-10 drop-shadow-md">+ 0.10 mL</span>
                </button>

                {/* Fast Addition Controls */}
                <button
                    onClick={() => handleAddVolume(1)}
                    disabled={!canTitrate}
                    className={`${fastBtn} py-4 text-lg col-span-1`}
                >
                    <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors" />
                    <span className="relative z-10 drop-shadow-md">+ 1.00 mL</span>
                </button>
                <button
                    onClick={() => handleAddVolume(5)}
                    disabled={!canTitrate}
                    className={`${fastBtn} py-4 text-xl col-span-1`}
                >
                    <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors" />
                    <span className="relative z-10 drop-shadow-md">+ 5.00 mL</span>
                </button>
            </div>

            {/* Utilities */}
            <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                    <button
                        onClick={resetExperiment}
                        className="interactable-btn flex items-center justify-center gap-2 flex-1 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/40 text-red-400 font-bold py-3 rounded-xl transition-all"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        Reset
                    </button>
                    <button
                        onClick={toggleStopcock}
                        disabled={!canTitrate}
                        className={`interactable-btn flex items-center justify-center gap-2 flex-1 font-bold py-3 rounded-xl transition-all disabled:opacity-50
                    ${isStopcockOpen
                                ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'}`}
                    >
                        <Power className={`w-4 h-4 ${isStopcockOpen ? 'animate-pulse' : ''}`} />
                        {isStopcockOpen ? 'Close Flow' : 'Continuous Flow'}
                    </button>
                </div>

                <button
                    onClick={async () => {
                        // VALIDATION
                        const questionsCount = discussionQuestions?.length || 0;
                        const answeredCount = Object.keys(discussionAnswers).filter(k => discussionAnswers[k]?.trim() !== '').length;
                        
                        if (questionsCount > 0 && answeredCount < questionsCount) {
                            // Optionally switch to the discussion tab for them using useUiStore
                            const uiStore = useUiStore.getState();
                            uiStore.setSidebarTab('exercises');
                            alert(`Please answer all ${questionsCount} discussion questions before finalizing your experiment.`);
                            return;
                        }
                        if (titrationData.length < 3) {
                            alert("Please plot sufficient titration data before finalizing the experiment.");
                            return;
                        }

                        setIsSubmitting(true);
                        
                        // Force a React flush and a frame paint so the Loader immediately displays
                        await new Promise(r => requestAnimationFrame(r));
                        await new Promise(r => setTimeout(r, 100));

                        setStopcockOpen(false);
                        setLabStage('done');

                        // 1. GRADE ALGORITHMIC LAB DATA
                        const bestVolumeEstimation = (flaskVolume * hclConcentration) / naohConcentration;
                        
                        // Calculate student's derived equivalence volume from their collected data points
                        let studentEquivVol = 0;
                        if (titrationData.length >= 2) {
                            let maxSlope = 0;
                            // Calculate steepest pH jump
                            for (let i = 1; i < titrationData.length; i++) {
                                const dv = titrationData[i].volume - titrationData[i - 1].volume;
                                const dph = titrationData[i].ph - titrationData[i - 1].ph;
                                if (dv > 0) {
                                    const slope = dph / dv;
                                    if (slope > maxSlope) {
                                        maxSlope = slope;
                                        studentEquivVol = (titrationData[i].volume + titrationData[i-1].volume) / 2;
                                    }
                                }
                            }
                        }
                        
                        const calcConc = (studentEquivVol * naohConcentration) / flaskVolume;
                        
                        const techniqueErrors: string[] = [];
                        if (studentEquivVol > bestVolumeEstimation * 1.1) {
                            techniqueErrors.push("Massively overshot the equivalence point.");
                        } else if (titrationData.length > 0 && titrationData.at(-1)!.ph < 7) {
                            techniqueErrors.push("Did not reach the equivalence point (pink color).");
                        }

                        const resultsData = gradeExperiment({
                            titrationData,
                            calculatedConcentration: calcConc,
                            expectedConcentration: hclConcentration,
                            techniqueErrors,
                            completionTimeSeconds: 600
                        });
                        
                        setScore(resultsData.totalScore);
                        
                        if (user && configData) {
                            // Save lab data points to Convex
                            await saveLabData({
                                experimentConfigId: configData._id,
                                studentId: user._id,
                                dataPoints: titrationData.map(d => ({ 
                                    volume: d.volume, 
                                    ph: d.ph, 
                                    calcConc,
                                    algorithmicScore: resultsData.totalScore,
                                    breakdown: resultsData.breakdown
                                })),
                            }).catch(console.error);

                            // 2. AUTO-GRADE AND SAVE DISCUSSION ANSWERS IF EXIST
                            if (questionsCount > 0) {
                                const isAutoGrading = configData.gradingMethod === 'auto' || !configData.gradingMethod;
                                let formattedAnswers: any[] = [];
                                
                                for (const q of discussionQuestions || []) {
                                    let autoGradeScore = undefined;
                                    let autoGradeFeedback = undefined;
                                    let answer = discussionAnswers[q._id] || "";
                                    
                                    if (isAutoGrading) {
                                        const gradeResult = await autoGradeDiscussionAnswer(
                                            q.question, 
                                            answer, 
                                            q.expectedKeyPoints
                                        );
                                        autoGradeScore = gradeResult.score;
                                        autoGradeFeedback = gradeResult.feedback;
                                        
                                        // Significant artificial delay to strictly enforce free-tier 15 RPM limits on Gemini
                                        await new Promise(r => setTimeout(r, 4500));
                                    }

                                    formattedAnswers.push({
                                        questionId: q._id as Id<"discussionQuestions">,
                                        answer,
                                        autoGradeScore,
                                        autoGradeFeedback
                                    });
                                }

                                await submitDiscussionMutation({
                                    studentId: user._id,
                                    experimentConfigId: configData._id,
                                    answers: formattedAnswers as any
                                }).catch(console.error);
                            }
                        }

                        setIsSubmitting(false);
                        navigate(`/student/results?id=${experimentId}`);
                    }}
                    disabled={volumeAdded < 0.1 || isSubmitting}
                    className="interactable-btn w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-indigo-500/20 mt-1"
                >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                    {isSubmitting ? 'Evaluating & Saving...' : 'Finish & Analyze Results'}
                </button>
            </div>

            {/* AI Grading Loader Overlay */}
            {aiLoaderOverlay}
        </div>
    );
}
